// Tiny ESM: unified camera fit math for both app and CLI.
// Avoid TS/types here; TS wrapper in src/core/cameraFit.ts provides types.
// No dependencies (the app's product code does not import three, docs/adr/0004): the few vector
// operations below are three.js r181's (MIT), on plain { x, y, z } and { x, y, z, w } objects.

const vec = (x, y, z) => ({ x, y, z })
const sub = (a, b) => vec(a.x - b.x, a.y - b.y, a.z - b.z)
const add = (a, b) => vec(a.x + b.x, a.y + b.y, a.z + b.z)
const scale = (a, s) => vec(a.x * s, a.y * s, a.z * s)
const cross = (a, b) => vec(a.y * b.z - a.z * b.y, a.z * b.x - a.x * b.z, a.x * b.y - a.y * b.x)
const lengthSq = (a) => a.x * a.x + a.y * a.y + a.z * a.z
const normalize = (a) => scale(a, 1 / (Math.sqrt(lengthSq(a)) || 1))

// Vector3.applyQuaternion
function applyQuaternion(v, q) {
  const tx = 2 * (q.y * v.z - q.z * v.y)
  const ty = 2 * (q.z * v.x - q.x * v.z)
  const tz = 2 * (q.x * v.y - q.y * v.x)
  return vec(
    v.x + q.w * tx + q.y * tz - q.z * ty,
    v.y + q.w * ty + q.z * tx - q.x * tz,
    v.z + q.w * tz + q.x * ty - q.y * tx,
  )
}

// Vector3.applyAxisAngle (axis is unit length)
function applyAxisAngle(v, axis, angle) {
  const s = Math.sin(angle / 2)
  return applyQuaternion(v, { x: axis.x * s, y: axis.y * s, z: axis.z * s, w: Math.cos(angle / 2) })
}

// Vector3.setFromSphericalCoords
function fromSpherical(radius, phi, theta) {
  const sinPhiRadius = Math.sin(phi) * radius
  return vec(sinPhiRadius * Math.sin(theta), Math.cos(phi) * radius, sinPhiRadius * Math.cos(theta))
}

// The quaternion of a camera at `eye` after camera.lookAt(target) with `up` (Object3D.lookAt for a
// camera: Matrix4.lookAt(eye, target, up), then Quaternion.setFromRotationMatrix).
function lookAtQuaternion(eye, target, up) {
  let z = sub(eye, target)
  if (lengthSq(z) === 0) z = vec(z.x, z.y, 1)
  z = normalize(z)
  let x = cross(up, z)
  if (lengthSq(x) === 0) {
    z = Math.abs(up.z) === 1 ? vec(z.x + 0.0001, z.y, z.z) : vec(z.x, z.y, z.z + 0.0001)
    z = normalize(z)
    x = cross(up, z)
  }
  x = normalize(x)
  const y = cross(z, x)
  const m11 = x.x, m12 = y.x, m13 = z.x
  const m21 = x.y, m22 = y.y, m23 = z.y
  const m31 = x.z, m32 = y.z, m33 = z.z
  const trace = m11 + m22 + m33
  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1.0)
    return { w: 0.25 / s, x: (m32 - m23) * s, y: (m13 - m31) * s, z: (m21 - m12) * s }
  } else if (m11 > m22 && m11 > m33) {
    const s = 2.0 * Math.sqrt(1.0 + m11 - m22 - m33)
    return { w: (m32 - m23) / s, x: 0.25 * s, y: (m12 + m21) / s, z: (m13 + m31) / s }
  } else if (m22 > m33) {
    const s = 2.0 * Math.sqrt(1.0 + m22 - m11 - m33)
    return { w: (m13 - m31) / s, x: (m12 + m21) / s, y: 0.25 * s, z: (m23 + m32) / s }
  }
  const s = 2.0 * Math.sqrt(1.0 + m33 - m11 - m22)
  return { w: (m21 - m12) / s, x: (m13 + m31) / s, y: (m23 + m32) / s, z: 0.25 * s }
}

const X_AXIS = vec(1, 0, 0)
const Y_AXIS = vec(0, 1, 0)
const Z_AXIS = vec(0, 0, 1)

const DEFAULT_TILT_PITCH = Math.PI * 0.22

export function computeCameraSnapshotForBounds(bounds, opts = {}) {
  const cameraMode = opts.cameraMode === 'tilt' ? 'tilt' : 'orbit'
  const margin = opts.margin ?? 1.1
  // Down to a hair off straight down (0 would make the y-up look-at degenerate): a sheet seen flat.
  const phi = Math.min(Math.max(opts.phi ?? Math.PI / 3, 0.0005), Math.PI / 2 - 0.05)
  const theta = opts.theta ?? Math.PI / 4
  const zoom = opts.zoom ?? 1.0
  const targetOverride = opts.target
  const targetYOffset = opts.targetYOffset ?? (targetOverride ? targetOverride.y : bounds.y / 2)
  const fov = opts.fov ?? 45

  let widthPx = 1280
  let heightPx = 800
  if (typeof window !== 'undefined') {
    widthPx = window.innerWidth || widthPx
    heightPx = window.innerHeight || heightPx
  }
  const canvasWidth = opts.canvasWidth ?? widthPx
  const canvasHeight = opts.canvasHeight ?? heightPx

  const target = targetOverride
    ? vec(targetOverride.x, targetOverride.y, targetOverride.z)
    : vec(bounds.x / 2, targetYOffset, bounds.z / 2)
  const width = Math.max(1, bounds.x)
  const height = Math.max(1, bounds.y)
  const depth = Math.max(1, bounds.z)

  const vFov = (fov * Math.PI) / 180
  const aspect = Math.max(0.1, canvasWidth / Math.max(1, canvasHeight))
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect)

  if (cameraMode === 'tilt') {
    const tiltPitch = opts.tiltPitch ?? DEFAULT_TILT_PITCH
    const tiltRoll = opts.tiltRoll ?? 0
    const tiltTurntable = opts.tiltTurntable ?? 0
    const halfWidth = (width * margin) / 2
    const halfDepth = (depth * margin) / 2
    const radiusX = halfWidth / Math.tan(Math.max(Math.PI / 180, hFov / 2))
    const radiusZ = halfDepth / Math.tan(Math.max(Math.PI / 180, vFov / 2))
    const zoomFactor = Math.max(0.01, zoom)
    let radius = Math.max(radiusX, radiusZ) / zoomFactor
    const minRadius = Math.max(4, height * margin)
    radius = Math.max(radius, minRadius)

    let forward = vec(0, -1, 0)
    forward = applyAxisAngle(forward, X_AXIS, tiltPitch)
    forward = applyAxisAngle(forward, Z_AXIS, tiltRoll)
    forward = applyAxisAngle(forward, Y_AXIS, tiltTurntable)

    let up = vec(0, 0, -1)
    up = applyAxisAngle(up, X_AXIS, tiltPitch)
    up = applyAxisAngle(up, Z_AXIS, tiltRoll)
    up = normalize(applyAxisAngle(up, Y_AXIS, tiltTurntable))

    const position = sub(target, scale(forward, radius))
    const near = 0.1
    const far = Math.max(200, (radius + height) * 2)
    const q = lookAtQuaternion(position, target, up)

    return {
      position: { x: position.x, y: position.y, z: position.z },
      quaternion: { x: q.x, y: q.y, z: q.z, w: q.w },
      fov,
      near,
      far,
      target: { x: target.x, y: target.y, z: target.z },
      up: { x: up.x, y: up.y, z: up.z },
      mode: 'tilt',
    }
  }

  const halfDiag = 0.5 * Math.hypot(width, height, depth)
  const halfAngle = Math.min(vFov, hFov) / 2
  let radius = halfDiag / Math.sin(Math.max(0.2, halfAngle))
  radius *= margin * zoom

  const position = add(target, fromSpherical(radius, phi, theta))

  const near = 0.1
  const far = Math.max(200, radius * 4)
  const q = lookAtQuaternion(position, target, Y_AXIS)

  return {
    position: { x: position.x, y: position.y, z: position.z },
    quaternion: { x: q.x, y: q.y, z: q.z, w: q.w },
    fov,
    near,
    far,
    target: { x: target.x, y: target.y, z: target.z },
    up: { x: 0, y: 1, z: 0 },
    mode: 'orbit',
  }
}

export function cellCenterTarget(bounds) {
  return {
    x: (bounds.x - 1) / 2,
    y: (bounds.y - 1) / 2,
    z: (bounds.z - 1) / 2,
  }
}
